import { useState, useEffect, useMemo } from 'react';

/**
 * Custom hook to handle pagination, search, and data accumulation for PickerField.
 *
 * @param {Object} config
 * @param {Function} config.fetchHook - The useXList hook (e.g., useCompanyDataList)
 * @param {Function} config.buildArgs - Function mapping pagination state to hook args: `(state) => [page, pageSize, searchKey, searchValue, ...]`
 * @param {Function} [config.formatOption] - Optional function to format raw data into options (e.g., { value, label })
 * @param {Object} [config.hookParams] - Optional additional dependencies
 * @returns {Object} - Props ready to be spread onto PickerField
 */
export default function usePickerPagination({
  fetchHook,
  buildArgs,
  formatOption,
  searchKey = 'name',
  searchPlaceholder,
  isInfiniteScroll = false,
  ...hookParams
}) {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [accumulatedData, setAccumulatedData] = useState([]);
  const [infiniteScrollData, setInfiniteScrollData] = useState([]);

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset pagination when dependencies change
  const depArray = Object.values(hookParams || {});
  useEffect(() => {
    setPage(0);
    setInfiniteScrollData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, pageSize, ...depArray]);

  // Call the store hook
  const args = buildArgs
    ? buildArgs({ page, pageSize, searchKey, searchValue: debouncedSearchText, ...hookParams })
    : [page, pageSize, searchKey, debouncedSearchText];
  const { data: pageData, totalRecord, isLoading } = fetchHook(...args);

  // Accumulate data for resolving selected labels
  useEffect(() => {
    let fetchedData = pageData;
    if (pageData && !Array.isArray(pageData) && Array.isArray(pageData.data)) {
      fetchedData = pageData.data;
    }

    if (Array.isArray(fetchedData) && fetchedData.length > 0) {
      if (isInfiniteScroll) {
        setInfiniteScrollData((prev) => {
          const newItems = [...prev];
          fetchedData.forEach((item) => {
            const itemId = item?.id ?? item?.customer_id ?? item?.value;
            const existingIndex = newItems.findIndex((p) => (p?.id ?? p?.customer_id ?? p?.value) === itemId);
            if (existingIndex < 0) {
              newItems.push(item);
            } else {
              newItems[existingIndex] = item;
            }
          });
          return newItems;
        });
      }

      setAccumulatedData((prev) => {
        const newItems = [...prev];
        fetchedData.forEach((item) => {
          const itemId = item?.id ?? item?.customer_id ?? item?.value;
          const existingIndex = newItems.findIndex((p) => (p?.id ?? p?.customer_id ?? p?.value) === itemId);
          if (existingIndex < 0) {
            newItems.push(item);
          } else {
            newItems[existingIndex] = item;
          }
        });
        return newItems;
      });
    }
  }, [pageData, isInfiniteScroll]);

  // Map accumulated options
  const options = useMemo(() => {
    return accumulatedData.map((item) => {
      if (formatOption) {
        const formatted = formatOption(item);
        return {
          ...formatted,
          companyName: formatted.companyName || item?.join?.company_name || item?.company_name || item?.companyName || '',
          raw: item,
          original: item
        };
      }
      return {
        value: item?.id ?? item?.value ?? String(item?.id ?? item?.value ?? ''),
        label: item?.license_plate ?? item?.name ?? item?.label ?? item?.customer ?? String(item?.id ?? item?.value ?? ''),
        companyName: item?.join?.company_name || item?.company_name || item?.companyName || '',
        raw: item,
        original: item
      };
    });
  }, [accumulatedData, formatOption]);

  // Map current page options for the list
  const listOptions = useMemo(() => {
    let fetchedData = pageData;
    if (pageData && !Array.isArray(pageData) && Array.isArray(pageData.data)) {
      fetchedData = pageData.data;
    }

    const sourceData = isInfiniteScroll ? infiniteScrollData : Array.isArray(fetchedData) ? fetchedData : [];

    return sourceData.map((item) => {
      if (formatOption) {
        const formatted = formatOption(item);
        return {
          ...formatted,
          companyName: formatted.companyName || item?.join?.company_name || item?.company_name || item?.companyName || '',
          raw: item,
          original: item
        };
      }
      return {
        value: item?.id ?? item?.value ?? String(item?.id ?? item?.value ?? ''),
        label: item?.license_plate ?? item?.name ?? item?.label ?? item?.customer ?? String(item?.id ?? item?.value ?? ''),
        companyName: item?.join?.company_name || item?.company_name || item?.companyName || '',
        raw: item,
        original: item
      };
    });
  }, [pageData, formatOption, isInfiniteScroll, infiniteScrollData]);

  const handleLoadMore = () => {
    if (isInfiniteScroll && listOptions.length < totalRecord && !isLoading) {
      setPage((p) => p + 1);
    }
  };

  return {
    options,
    listOptions,
    page,
    searchKey,
    ...(searchPlaceholder ? { searchPlaceholder } : {}),
    ...(isInfiniteScroll ? {} : { onPageChange: (newPage) => setPage(newPage) }),
    ...(isInfiniteScroll ? {} : { pageSizeOptions: [10, 25, 50, 100, 250, 500, 1000] }),
    ...(isInfiniteScroll ? {} : { onPageSizeChange: (sz) => setPageSize(sz) }),
    ...(isInfiniteScroll ? { onLoadMore: handleLoadMore } : {}),
    pageSize,
    onSearch: (text) => setSearchText(text),
    totalRecord,
    isLoading
  };
}
